/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../../../platform/Platform';
import { ComponentManagerFn } from '../../../../registry/Component.type';
import { ItemType } from '../../../../registry/ComponentRegistry';
import { baseComponentManager } from '../../../../registry/Components';
import { PivotColumnDefaultProps } from '../PivotTableStringColumn/PivotTableStringColumnManager';
import { defaultValue } from './PivotTableHiddenColumn';

const PivotTableHiddenColumnManager: ComponentManagerFn<
  'PivotTableHiddenColumn'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'PivotTableHiddenColumn'>(
      platform,
      'PivotTableHiddenColumn',
    ),
    type: 'PivotTableHiddenColumn',
    name: 'Pivot Table Hidden Column',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnClick'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...PivotColumnDefaultProps,
      viewAttribute: {
        ...PivotColumnDefaultProps.viewAttribute,
        getAttributeDataTypes: () => 'ALL',
      },
    },
    isPropertyVisible: (propName) => {
      if (propName === 'viewAttribute') {
        return true;
      }
      return false;
    },
    getDisplayText: (item) => item.props.viewAttribute,
    category: 'Forms',
    icon: { iconName: 'eye-slash' },
    description: `**Overview**<br/>
The Pivot Table Hidden Column is a utility component used strictly within a **Pivot Table**. As its name implies, it adds a data field to the Pivot Table's internal data model without rendering it visibly in the grid.
Use this component when you need to access a specific data point for calculations, sorting logic, or triggering actions, but do not want to clutter the visual report with that specific column.

**1. Data Binding**<br/>
Like visible columns, this component binds to a field in your Data Store.
* **View Attribute:** Select the Data Store field to include in the pivot logic.
* **Supported Types:** All attribute types (**ALL**) are supported.

**2. Configuration**<br/>
Since the column is invisible, all standard display properties (like label, width, color) are hidden from the configuration panel. Only the **viewAttribute** is exposed.
* **Data Presence:** Even though it is not rendered on screen, the data is still fetched and available to the Pivot Table's internal engine for processing.

**3. Best Use Cases**<br/>
* **Sorting Logic:** Binding a "SortOrder" integer field to control the display order of a visible "Status" text column (e.g., ensuring "Pending" appears before "Approved" even though "A" comes before "P").
* **Calculated Fields:** Providing raw data (like "Unit Cost") that is used by a **PivotTableCalculatedColumn** formula but doesn't need to be seen by the end-user.
* **Context for Actions:** Storing a hidden "Record ID" so that when a user clicks a visible row or cell, the system knows exactly which database record to update or navigate to.`,
  };
};

export default PivotTableHiddenColumnManager;
